/**
 * ONE COMPOSE BRAIN — Masters Ladder R1 (docs/MASTERS_LADDER_PLAN.md §1.3).
 *
 * Pure. Zero I/O. No imports beyond shared types, so the canvas, the export
 * path, the word engine and the migration backfill all resolve a master the
 * SAME way — the zone-layout / body-layout one-brain pattern.
 *
 * Composition order (law):
 *
 *     core blocks → client layer (adds/overrides/hides)
 *
 *  - Hides NEVER delete. `compose()` keeps them in the list flagged
 *    `hidden: true` so editor surfaces can draw the ghost row with "restore";
 *    `toExportBlocks()` is the strict list and drops them.
 *
 *  Phase 2 (2026-08-25) demolished the Selections axis: there are no slots,
 *  options or picks to resolve any more, so composition is core + one layer.
 */

import type {
  BlockOverride,
  BlockOverridePayload,
  ClientLayer,
  ContentBlock,
  SpecMaster,
} from './spec';

// ─── Composed output ───

export type BlockOrigin = 'core' | 'layer';

/** Why a block is hidden. Only a client layer can hide one. */
export type HiddenReason = 'layer';

export interface OverrideProvenance {
  /** Id of the `BlockOverride` that shadows this block. */
  by: string;
  /** The base block changed since the override was written. */
  stale?: boolean;
}

/** A block plus its provenance. Editor surfaces read this; exports read
 *  `toExportBlocks()`. */
export interface ComposedBlock {
  block: ContentBlock;
  origin: BlockOrigin;
  /** Set on `origin === 'layer'` blocks: whose layer. */
  layerId?: string;
  clientId?: string;
  overridden?: OverrideProvenance;
  /** Ghost — present for the editor, absent from the export. */
  hidden?: boolean;
  hiddenReason?: HiddenReason;
}

/** Everything `compose()` learned along the way, for panels and reports. */
export interface ComposeResult {
  blocks: ComposedBlock[];
  /** Overrides whose base block no longer matches the captured snapshot. */
  staleOverrideIds: string[];
  /** Override / hide / add ids that pointed at a block the composition does
   *  not contain. Never fatal — surfaced so the Studio can clean up. */
  orphanOverrideIds: string[];
  orphanHideIds: string[];
}

// ─── Content hashing (stale detection) ───

/** Fields that make a block's CONTENT. Provenance, comments and transient
 *  revision marks are deliberately excluded — a core block does not become
 *  "stale" because someone re-tagged it. */
export const CONTENT_KEYS = [
  'text',
  'level',
  'bold',
  'italic',
  'underline',
  'highlight',
  'runs',
  'blockType',
  'tableData',
  'imageData',
  'imageWidth',
  'continuation',
  'excluded',
] as const;

/**
 * STRUCTURE, not content — fields a client layer must be able to express on a
 * baseline block even though they are deliberately NOT part of its content.
 *
 * `groupId` is the only one: grouping is the host document's own structure, so
 * it must ride an override (or a variant's own group would vanish on reload),
 * yet it must never hash as content. If it did, grouping a block that arrived
 * from a module would read as "this copy was edited locally" (spec-modules.ts
 * drift), and an office master grouping three paragraphs would raise a
 * "modified" update against every variant that holds them.
 */
export const STRUCTURE_KEYS = ['groupId'] as const;

/** What an override CARRIES: content plus structure. `hashBlock` stays on
 *  CONTENT_KEYS alone — the two lists are deliberately different. */
export const OVERRIDE_KEYS = [...CONTENT_KEYS, ...STRUCTURE_KEYS] as const;

/** Typed key/value view of a block — the ONE place the structural cast lives,
 *  so no consumer needs `any` to read or strip a field by name. */
function asRecord(block: Partial<ContentBlock>): Record<string, unknown> {
  return block as unknown as Record<string, unknown>;
}

export function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return '[' + value.map(stableStringify).join(',') + ']';
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj)
      .filter((k) => obj[k] !== undefined)
      .sort();
    return '{' + keys.map((k) => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') + '}';
  }
  return JSON.stringify(value);
}

/** FNV-1a 32-bit over the block's canonical content. Deterministic across
 *  processes — the same block always hashes the same, so an override written
 *  in the renderer is comparable in the engine or a migration script. */
export function hashBlock(block: Partial<ContentBlock> | undefined): string {
  const src = block || {};
  const picked: Record<string, unknown> = {};
  for (const key of CONTENT_KEYS) {
    const v = asRecord(src)[key];
    if (v !== undefined) picked[key] = v;
  }
  const str = stableStringify(picked);
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Capture the base snapshot an override needs for stale detection. Call this
 *  when the override is CREATED or reverted-to-current, never at compose time. */
export function makeOverrideBase(block: Partial<ContentBlock>): {
  hash: string;
  fields: BlockOverridePayload;
  capturedAt: string;
} {
  const fields: Record<string, unknown> = {};
  // OVERRIDE_KEYS, not CONTENT_KEYS: the payload also carries the block's
  // group so a variant's own grouping survives a reload. The HASH above stays
  // content-only — see STRUCTURE_KEYS.
  for (const key of OVERRIDE_KEYS) {
    const v = asRecord(block)[key];
    if (v !== undefined) fields[key] = v;
  }
  return {
    hash: hashBlock(block),
    fields: fields as BlockOverridePayload,
    capturedAt: new Date().toISOString(),
  };
}

// ─── Compose ───

function cloneBlock(block: ContentBlock): ContentBlock {
  return JSON.parse(JSON.stringify(block)) as ContentBlock;
}

/**
 * Stack an override on its core block.
 *
 * `groupId` is the ONE field the payload does not simply win: grouping is
 * structure, and the OFFICE MASTER owns the structure of its own blocks. So
 * the payload's group is applied only where the master has declared none AND
 * the value is the variant's own invention (it differs from what the master
 * said when the override was captured). Everywhere else the master's value
 * wins — including "no group at all".
 *
 * Without that rule an old payload outranks the live master three ways: a
 * master that groups a block a variant had already overridden gets its group
 * split, a master that REgroups one has the old id forced back on, and a
 * master that ungroups one leaves a phantom one-block group behind. Each of
 * those also makes the sheet disagree with `masterGroupIds`, which is what
 * the variant guard compares against — so the variant would refuse every
 * edit from then on.
 */
function applyPayload(
  block: ContentBlock,
  payload: BlockOverridePayload,
  baseFields?: BlockOverridePayload
): ContentBlock {
  const next = cloneBlock(block);
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || key === 'groupId') continue;
    asRecord(next)[key] = JSON.parse(JSON.stringify(value)) as unknown;
  }
  const coreGroup = ((block.groupId as string | undefined) || '').trim();
  const payloadGroup = ((asRecord(payload).groupId as string | undefined) || '').trim();
  const baseGroup = ((baseFields && (asRecord(baseFields).groupId as string)) || '').trim();
  if (!coreGroup && payloadGroup && payloadGroup !== baseGroup) next.groupId = payloadGroup;
  next.id = block.id;
  return next;
}

/**
 * Compose a master into the block list a variant shows.
 *
 * @param master The office master (core blocks).
 * @param layer  The client layer to stack on top, if any.
 */
export function compose(
  master: Pick<SpecMaster, 'id' | 'coreContent'>,
  layer?: ClientLayer
): ComposeResult {
  const core = master.coreContent || [];

  // ── 1. Core blocks ──
  const out: ComposedBlock[] = core.map((raw) => ({
    block: cloneBlock(raw),
    origin: 'core' as const,
  }));

  const indexOfBlock = (id: string): number => out.findIndex((c) => c.block.id === id);

  const staleOverrideIds: string[] = [];
  const orphanOverrideIds: string[] = [];
  const orphanHideIds: string[] = [];

  if (layer) {
    // ── 3a. Hides → ghosts (never deleted) ──
    for (const blockId of layer.hides || []) {
      const idx = indexOfBlock(blockId);
      if (idx < 0) {
        orphanHideIds.push(blockId);
        continue;
      }
      out[idx].hidden = true;
      out[idx].hiddenReason = 'layer';
    }

    // ── 3b. Overrides (stale detection) ──
    for (const override of layer.overrides || []) {
      const idx = indexOfBlock(override.blockId);
      if (idx < 0) {
        orphanOverrideIds.push(override.id);
        continue;
      }
      const stale = !!override.base?.hash && hashBlock(out[idx].block) !== override.base.hash;
      if (stale) staleOverrideIds.push(override.id);
      out[idx].block = applyPayload(out[idx].block, override.payload, override.base?.fields);
      out[idx].overridden = { by: override.id, ...(stale ? { stale } : {}) };
    }

    // ── 3c. Adds, anchored after their neighbour ──
    //    Multiple adds sharing an anchor keep their authored order; a missing
    //    anchor falls back to `index`, then to the end of the document.
    const cursor = new Map<string, number>();
    for (const add of layer.adds || []) {
      const anchorId = add.afterBlockId;
      let pos: number;
      const known = anchorId ? cursor.get(anchorId) : undefined;
      if (known !== undefined) {
        pos = known + 1;
      } else {
        const idx = anchorId ? indexOfBlock(anchorId) : -1;
        if (idx >= 0) pos = idx + 1;
        else if (typeof add.index === 'number') pos = Math.max(0, Math.min(out.length, add.index));
        else pos = out.length;
      }
      out.splice(pos, 0, {
        block: cloneBlock(add.block),
        origin: 'layer',
        layerId: layer.id,
        clientId: layer.clientId,
      });
      // Everything anchored at or after the insertion point shifted right.
      for (const [key, val] of cursor) {
        if (val >= pos) cursor.set(key, val + 1);
      }
      if (anchorId) cursor.set(anchorId, pos);
    }
  }

  return {
    blocks: out,
    staleOverrideIds,
    orphanOverrideIds,
    orphanHideIds,
  };
}

// ─── Export list ───

/** DEAD stamps that must never reach the engine or a .docx. Phase 2 removed
 *  all six from `ContentBlock`, but rows written before the materializer ran
 *  still carry them in stored JSON — so the scrub stays, belt and braces. */
const STRIPPED_KEYS = [
  'isVariant',
  'variantId',
  'variantIds',
  'excludedFromVariants',
  'slotId',
  'optionId',
  'moduleStamp',
] as const;

/**
 * Dead stamps off an ALREADY-flat list (a project spec's blocks, which were
 * composed when the project forked). The engine must never meet a ladder
 * marker; this is the same law as `toExportBlocks`, applied where there is
 * nothing left to compose. R5: the export pipeline runs every spec through it.
 */
export function stripMembership(
  blocks: ContentBlock[] | undefined,
  opts?: { keepModuleStamps?: boolean }
): ContentBlock[] {
  return (blocks || []).map((raw) => {
    const block = cloneBlock(raw);
    for (const key of STRIPPED_KEYS) {
      if (opts?.keepModuleStamps && key === 'moduleStamp') continue;
      delete asRecord(block)[key];
    }
    return block;
  });
}

/**
 * The strict list: ghosts gone, overrides applied, every dead stamp scrubbed.
 * This is what exports and the word engine consume — they never see the
 * ladder.
 */
export function toExportBlocks(
  composed: ComposeResult | ComposedBlock[],
  opts?: { keepModuleStamps?: boolean }
): ContentBlock[] {
  const list = Array.isArray(composed) ? composed : composed.blocks;
  const result: ContentBlock[] = [];
  for (const c of list) {
    if (c.hidden) continue;
    const block = cloneBlock(c.block);
    for (const key of STRIPPED_KEYS) {
      if (opts?.keepModuleStamps && key === 'moduleStamp') continue;
      delete asRecord(block)[key];
    }
    result.push(block);
  }
  return result;
}
